const DEFAULT_THRESHOLDS = {
  rage_clicks: 3,
  rage_window_ms: 1000,
  rage_radius_px: 24,
  dead_click_ms: 1000,
  slow_xhr_ms: 1500,
  idle_rotation_minutes: 30,
}

const DEFAULT_FLUSH_MS = 30000
const MAX_INTERNAL_ERRORS = 20

let current = null
let token = null
let debug = null
let internalErrors = 0

// Shared mutable state, kept here so modules can read it without importing each other.
export const state = {
  disabled: false,
  booted: false,
  installed: false,
  page: null,
  pageStartedAt: 0,
  pageLeft: false,
  correlationId: null,
  confirmOpen: false,
  lastTrigger: null,
  lastKey: null,
  lastPointer: null,
  lastNavigationAt: 0,
  lastInputAt: 0,
  idleGap: false,
  stats: { clicks: 0, xhr: 0 },
}

export function readConfig(page) {
  const config = page?.props?.helios

  if (
    !config ||
    typeof config !== 'object' ||
    typeof config.token !== 'string' ||
    typeof config.endpoint !== 'string'
  ) {
    return null
  }

  return config
}

export function getConfig() {
  return current
}

export function setConfig(config) {
  current = config ?? null
}

export function parseToken(value) {
  const parts = typeof value === 'string' ? value.split('.') : []

  if (parts.length !== 3) {
    return null
  }

  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const claims = JSON.parse(window.atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')))
    const exp = Number(claims?.e)

    if (!Number.isFinite(exp)) {
      return null
    }

    return { value, user: Number.isInteger(claims.u) ? claims.u : null, exp }
  } catch {
    return null
  }
}

export function getToken() {
  return token
}

export function setToken(next) {
  token = next
}

export function threshold(name) {
  const value = Number(current?.thresholds?.[name])

  return Number.isFinite(value) && value > 0 ? value : DEFAULT_THRESHOLDS[name]
}

export function flushInterval() {
  const value = Number(current?.flush_ms)

  return Number.isFinite(value) && value >= 1000 ? value : DEFAULT_FLUSH_MS
}

export function sampleRate() {
  const value = Number(current?.sample_rate)

  return Number.isFinite(value) ? value : 1
}

export function currentContext() {
  return {
    route_name: state.page?.routeName ?? null,
    component: state.page?.component ?? null,
    path: state.page?.path ?? null,
    correlation_id: state.correlationId,
    masked: current?.mask_labels === true,
  }
}

export function isDebug() {
  if (debug === null) {
    try {
      debug = window.localStorage.getItem('helios:debug') === '1'
    } catch {
      debug = false
    }
  }

  return debug
}

export function noteInternalError(error) {
  internalErrors += 1

  if (isDebug()) {
    console.debug('[helios] internal error', error)
  }

  if (internalErrors >= MAX_INTERNAL_ERRORS) {
    state.disabled = true
  }
}

export function safe(fn) {
  return function (...args) {
    try {
      return fn.apply(this, args)
    } catch (error) {
      noteInternalError(error)

      return undefined
    }
  }
}
